// Fixed-slot concatenation of several branches' packed splat words into ONE
// buffer for a single rasterizer call.
//
// The GPU lift's packed layout (wgsl/lift_pass2.wgsl) gives each texel a FIXED
// slot and clears culled texels to transparent, so Spark's async depth sort can
// keep its index→splat map across frames. Concatenation must preserve that
// property: every branch owns a base offset for the whole session, and a branch
// that stops rendering (the blueprint's region toggle) is ZEROED in place rather
// than dropped — dropping it would shift every later branch's slots and both
// tear the sort and shrink the buffer past SplatMeshUpdater's fixed capacity.
//
// Packed centers are absolute pack2x16float halves, so a concat is a pure word
// append — no re-basing. The output buffer is allocated once and reused: this
// runs per rig update, and CLAUDE.md forbids per-frame allocation on the hot
// path (the SoA concatGaussians allocates five fresh Float32Arrays, ~34 MB).

import type { Gaussians } from "../inference/lift";

const WORDS_PER_SPLAT = 4;

export class PackedConcat {
  /** Per-branch slot capacity, in declaration order. */
  readonly capacities: readonly number[];
  /** Per-branch base slot offset — constant for the session. */
  readonly bases: readonly number[];
  /** Total slot capacity == the SplatMesh's fixed capacity. */
  readonly capacity: number;

  private readonly packed: Uint32Array;
  // Reused result object; only `packed`/`count` are meaningful in packed mode,
  // but Gaussians requires the SoA fields, so they stay empty views.
  private readonly out: Gaussians;
  // Which branch ranges currently hold real data — so a branch that goes
  // inactive is zeroed exactly ONCE, not re-zeroed every frame.
  private readonly filled: boolean[];

  constructor(capacities: readonly number[]) {
    this.capacities = [...capacities];
    const bases: number[] = [];
    let base = 0;
    for (const c of this.capacities) {
      bases.push(base);
      base += c;
    }
    this.bases = bases;
    this.capacity = base;
    this.packed = new Uint32Array(this.capacity * WORDS_PER_SPLAT);
    this.filled = this.capacities.map(() => false);
    const empty = new Float32Array(0);
    this.out = {
      positions: empty,
      scales: empty,
      rotations: empty,
      colors: empty,
      opacities: empty,
      count: this.capacity,
      packed: this.packed,
    };
  }

  /** Concatenate `parts[i]` (a packed Gaussians, or null/undefined for a branch
   *  that isn't rendering this frame) into the persistent buffer. Returns the
   *  reused result object — the caller must consume it before the next call
   *  (SplatMeshUpdater snapshots it). */
  concat(parts: readonly (Gaussians | null | undefined)[]): Gaussians {
    if (parts.length !== this.capacities.length) {
      throw new Error(`PackedConcat: expected ${this.capacities.length} sources, got ${parts.length}`);
    }
    for (let i = 0; i < parts.length; i++) {
      const g = parts[i];
      const base = this.bases[i] * WORDS_PER_SPLAT;
      const words = this.capacities[i] * WORDS_PER_SPLAT;
      if (g?.packed) {
        if (g.packed.length < words) {
          throw new Error(
            `PackedConcat: source ${i} has ${g.packed.length / WORDS_PER_SPLAT} slots, declared ${this.capacities[i]}`,
          );
        }
        this.packed.set(g.packed.subarray(0, words), base);
        this.filled[i] = true;
      } else if (this.filled[i]) {
        // Inactive branch: clear its slots to transparent (word 0 = rgba8 with
        // alpha 0) and keep the capacity — never shrink the buffer.
        this.packed.fill(0, base, base + words);
        this.filled[i] = false;
      }
    }
    return this.out;
  }
}
